import { useId, useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_AXIS_TICK } from "../../utils/chartConfig";

export type GlucoseCurveChartPoint = {
  t: number;
  noAction: number | null;
  withAction: number | null;
};

interface GlucoseCurveComparisonPanelProps {
  chartData: GlucoseCurveChartPoint[];
  noActionPeak: number;
  withActionPeak: number;
  dropMgDl: number;
  activityLabel: string;
  nowGl?: number;
  chartHeightClass?: string;
  /** Hide bottom NO ACTION / activity count row (e.g. closed loop uses top card only) */
  showPeakComparison?: boolean;
  withActionStroke?: string;
  noActionStrokeDash?: string;
  /** Smaller inline legend for compact cards (e.g. Idle last meal) */
  compactLegend?: boolean;
  /** Idle last meal: dashed red peak line + green filled bent curve */
  idleChartMode?: boolean;
}

const CurveTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ dataKey: string; name: string; value: number; stroke: string }>;
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-card/95 px-3 py-2 shadow-lg backdrop-blur-sm">
      <p className="mb-1 text-compact-xs font-semibold text-muted-foreground">{label}m</p>
      {payload.map((entry) => (
        <p
          key={entry.dataKey}
          className="text-compact-xs font-bold"
          style={{ color: entry.stroke }}
        >
          {entry.name}: {entry.value} mg/dL
        </p>
      ))}
    </div>
  );
};

const GlucoseCurveComparisonPanel = ({
  chartData,
  noActionPeak,
  withActionPeak,
  dropMgDl,
  activityLabel,
  nowGl,
  chartHeightClass = "h-40",
  showPeakComparison = true,
  withActionStroke = "#6b7280",
  noActionStrokeDash,
  compactLegend = false,
  idleChartMode = false,
}: GlucoseCurveComparisonPanelProps) => {
  const chartGradId = useId().replace(/:/g, "");
  const withActionFill = withActionStroke;

  const hasRenderableChart = useMemo(() => {
    const valid = chartData.filter(
      (p) =>
        (p.noAction != null && Number.isFinite(Number(p.noAction))) ||
        (p.withAction != null && Number.isFinite(Number(p.withAction)))
    );
    return valid.length >= 2;
  }, [chartData]);

  if (!hasRenderableChart) return null;

  return (
    <div className="space-y-0">
      <div className={chartHeightClass}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={`glucoseGradient-${chartGradId}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
              <linearGradient
                id={`interventionGradient-${chartGradId}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={withActionFill} stopOpacity={0.2} />
                <stop offset="95%" stopColor={withActionFill} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" className="text-border/30" />
            <XAxis
              dataKey="t"
              stroke="currentColor"
              className="text-muted-foreground text-compact-xs"
              tick={CHART_AXIS_TICK}
              tickFormatter={(v) => `${v}m`}
            />
            <YAxis
              stroke="currentColor"
              className="text-muted-foreground text-compact-xs"
              tick={CHART_AXIS_TICK}
              domain={["auto", "auto"]}
              width={32}
            />
            <Tooltip content={<CurveTooltip />} />
            {nowGl != null && (
              <ReferenceLine y={nowGl} stroke="#666" strokeDasharray="3 3" />
            )}
            <Area
              type="monotone"
              dataKey="noAction"
              stroke="#ef4444"
              strokeWidth={idleChartMode ? 2 : 2.5}
              strokeDasharray={noActionStrokeDash ?? (idleChartMode ? "5 4" : undefined)}
              fill={idleChartMode ? "transparent" : `url(#glucoseGradient-${chartGradId})`}
              name="No Action"
              connectNulls
            />
            <Area
              type="monotone"
              dataKey="withAction"
              stroke={withActionStroke}
              strokeWidth={2.5}
              fill={`url(#interventionGradient-${chartGradId})`}
              name={`With ${activityLabel}`}
              connectNulls
            />
            {nowGl != null && (
              <ReferenceDot
                x={0}
                y={nowGl}
                r={4}
                fill="#000"
                stroke="#fff"
                strokeWidth={2}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {!compactLegend ? (
        <div className="mt-3 flex items-center justify-center gap-4 text-xs">
          <div className="flex items-center gap-1.5">
            <div className="h-0.5 w-6 bg-red-500" />
            <span className="font-medium text-muted-foreground">No Action</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="h-0.5 w-6"
              style={{ backgroundColor: withActionStroke }}
            />
            <span className="font-medium text-muted-foreground">
              With {activityLabel}
            </span>
          </div>
        </div>
      ) : null}

      {showPeakComparison ? (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-red-50 p-3 text-center dark:bg-red-950/20">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
              No Action
            </div>
            <div className="text-3xl font-black text-red-500">{Math.round(noActionPeak)}</div>
          </div>
          <div className="relative rounded-lg bg-green-50 p-3 text-center dark:bg-green-950/20">
            <div className="absolute -left-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-green-500 text-white shadow-lg">
              →
            </div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-green-600 dark:text-green-400">
              {activityLabel}
            </div>
            <div className="flex items-baseline justify-center gap-1">
              <div className="text-3xl font-black text-green-600">
                {Math.round(withActionPeak)}
              </div>
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-green-600 text-xs font-bold text-white">
                -{Math.round(dropMgDl)}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default GlucoseCurveComparisonPanel;
